/**
 * A profile is a record, not code (docs/05-kern-und-profile.md, P1-3): its fields, reasons,
 * packaging units, suggested storages, how it counts and the words the app uses. An app such as
 * KellerLog is the core with one profile; a second profile loads without changing the code
 * (test/profile.test.ts loads one from JSON).
 */
import { REASON_CLASSES } from "./reasons.ts";
import type { Direction, Reason, ReasonClass } from "./reasons.ts";
import type { CountMode } from "./model.ts";
import type { ProfileMeasures } from "./measures.ts";

export type FieldType = "text" | "number" | "year" | "choice" | "multi" | "yesno" | "money" | "range" | "longtext" | "glass" | "size" | "alcohol" | "base";

export interface ProfileField {
  key: string;
  label: string;
  type: FieldType;
  /** "family": the same for every vintage (or edition); "article": per article. */
  level: "family" | "article";
  required?: boolean;
  /** Shown as filter in lists. */
  filter?: boolean;
  /** Fixed choices; for "text" suggestions that may be overridden. */
  options?: readonly string[];
  /** For "size": the sizes offered, in ml (stored as a number of ml). */
  sizes?: readonly number[];
  unit?: string;
  hint?: string;
  /** Section of the form; a key of the profile's `groups`. */
  group: string;
  /** Other words in a private cellar or household (B7): „Nummer“ instead of „Kartennummer“. */
  private?: { label?: string; hint?: string };
}

/** For whom a reason is offered: a business, a private household or cellar, or both. */
export type ReasonFor = "both" | "business" | "private";
export type ProfileReason = Reason & { for: ReasonFor };
export type WorkspaceKind = "business" | "private";

export interface Words {
  one: string;
  the: string;
  of: string;
}

export interface Profile {
  id: string;
  /** The app's name, e.g. "KellerLog". */
  app: string;
  vocabulary: {
    /** What an article is called: "Wein", "Weine". */
    article: string;
    articles: string;
    /** The container counted in (the base unit is ml): "Flasche", "Flaschen", short "Fl.". */
    unit: string;
    units: string;
    unitShort: string;
    /** The word for taking out, per kind of workspace: "Entnehmen", privately "Trinken". */
    take: Record<WorkspaceKind, string>;
    /** What a booking of taking out is called afterwards: „Verbraucht“ in a household's pantry; without it „Entnommen“. */
    taken?: Record<WorkspaceKind, string>;
    /** The workspace: "Betrieb" or "Keller". */
    workspace: Record<WorkspaceKind, Words>;
    /** Placeholder of the search for an article. */
    search: string;
  };
  fields: readonly ProfileField[];
  groups: Record<string, string>;
  /** Fields that make the article's name, in order (missing year: `nameWithout`). */
  nameFields: readonly string[];
  /** Shown when a year field of the name is empty, e.g. "o. J.". */
  nameWithout?: string;
  /** The field that is the category (filter chips, sorting). */
  categoryField?: string;
  /**
   * Packaging units, the factor in containers (Karton à 6: 6); an article stores it in ml. With
   * `sizes` a new article gets the pack only in these sizes (a crate of 20 holds 0,5-l bottles),
   * with `containers` only in these containers (a crate holds bottles, a tray cans, a keg nothing).
   */
  packs: readonly { name: string; plural: string; factor: number; sizes?: readonly number[]; containers?: readonly string[] }[];
  /**
   * From this size on (ml), a drink without container is in this one: beer of 30 l is a keg, not a
   * bottle. `reasons`: what the large container goes out with: reasons left out, labels renamed
   * (a keg is poured when it is tapped, never sold as a bottle); the ids stay those of the core.
   */
  bulk?: { from: number; container: string; reasons?: { without: readonly string[]; labels: Record<string, string> } };
  /** Names of serving and container sizes (measures.ts), for the profile and its kinds. */
  measures?: ProfileMeasures;
  /** The usual size in ml: a name shows the size only when it differs („(1,5 l)“); none: always. */
  usualSize?: number;
  /** Fields a new article starts with (allergens: Sulfite for wine). */
  defaults?: Record<string, unknown>;
  /** Its goods carry a best-before date (MHD) on receipt (docs/17-getraenke.md, Fristen). */
  expires?: boolean;
  /** The first of each direction is the default. */
  reasons: readonly ProfileReason[];
  tracking: "lot" | "quantity";
  countMode: CountMode;
  /** Storages to start with. */
  starter: readonly { id: string; name: string; locations: readonly (readonly [string, string])[] }[];
}

/** A profile's fields with the words of the kind of workspace: in a private cellar „Nummer“, not „Kartennummer“. */
export function profileFields(profile: Profile, kind: WorkspaceKind = "business"): ProfileField[] {
  return profile.fields.map((f) => (kind === "private" && f.private ? { ...f, ...f.private } : f));
}

/** The reasons a profile offers for a direction and kind of workspace, default first. */
export function profileReasons(profile: Profile, direction: Direction, kind: WorkspaceKind = "business"): ProfileReason[] {
  return profile.reasons.filter((r) => REASON_CLASSES[r.class]?.direction === direction && (r.for === "both" || r.for === kind));
}

/** An article's name from the profile's name fields: "Erzeuger Name Jahrgang". */
export function profileName(profile: Profile, f: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const key of profile.nameFields) {
    const v = f[key];
    const field = profile.fields.find((x) => x.key === key);
    if (typeof v === "string" && v.trim() !== "") parts.push(v.trim());
    else if (typeof v === "number") parts.push(String(v));
    else if (field?.type === "year" && profile.nameWithout) parts.push(profile.nameWithout);
  }
  return parts.join(" ");
}

/**
 * What must hold for a profile to load: unique field keys within the server's limit, groups that
 * exist, reasons in classes of the core with a default in and out for each kind of workspace,
 * name fields that exist, packaging factors above 1.
 */
export function checkProfile(profile: Profile): string[] {
  const errors: string[] = [];
  const keys = profile.fields.map((f) => f.key);
  const dup = keys.filter((k, i) => keys.indexOf(k) !== i);
  if (dup.length) errors.push(`Felder doppelt: ${[...new Set(dup)].join(", ")}`);
  for (const f of profile.fields) {
    if (f.key.length > 36) errors.push(`Feld ${f.key}: Name zu lang`);
    if (!(f.group in profile.groups)) errors.push(`Feld ${f.key}: Gruppe ${f.group} fehlt`);
    if ((f.type === "choice" || f.type === "multi") && !f.options?.length) errors.push(`Feld ${f.key}: Auswahl ohne Werte`);
    if (f.type === "size" && !f.sizes?.every((ml) => Number.isInteger(ml) && ml > 0)) errors.push(`Feld ${f.key}: Größen in ganzen ml fehlen`);
  }
  for (const k of profile.nameFields) if (!keys.includes(k)) errors.push(`Namensfeld ${k} fehlt`);
  if (profile.categoryField && !keys.includes(profile.categoryField)) errors.push(`Kategoriefeld ${profile.categoryField} fehlt`);
  const ids = profile.reasons.map((r) => r.id);
  if (new Set(ids).size !== ids.length) errors.push("Gründe doppelt");
  for (const r of profile.reasons) if (!(r.class in REASON_CLASSES)) errors.push(`Grund ${r.id}: Grundklasse ${r.class as ReasonClass} gibt es nicht`);
  for (const kind of ["business", "private"] as const) {
    for (const direction of ["in", "out"] as const) {
      if (profileReasons(profile, direction, kind).length === 0) errors.push(`Kein Grund für ${direction === "in" ? "Zugang" : "Abgang"} (${kind})`);
    }
  }
  for (const p of profile.packs) if (!(Number.isInteger(p.factor) && p.factor > 1)) errors.push(`Verpackung ${p.name}: Faktor muss eine ganze Zahl über 1 sein`);
  const containers = profile.fields.find((f) => f.key === "container")?.options ?? [];
  for (const p of profile.packs) for (const c of p.containers ?? []) if (!containers.includes(c)) errors.push(`Verpackung ${p.name}: Gebinde ${c} fehlt`);
  if (profile.bulk && !containers.includes(profile.bulk.container)) errors.push(`Gebinde ${profile.bulk.container} fehlt`);
  const bulk = profile.bulk?.reasons;
  for (const id of [...(bulk?.without ?? []), ...Object.keys(bulk?.labels ?? {})]) if (!ids.includes(id)) errors.push(`Gebinde ${profile.bulk!.container}: Grund ${id} gibt es nicht`);
  for (const kind of ["business", "private"] as const) {
    if (bulk && profileReasons(profile, "out", kind).every((r) => bulk.without.includes(r.id))) errors.push(`Gebinde ${profile.bulk!.container}: kein Grund für Abgang (${kind})`);
  }
  return errors;
}

/** The container of a drink: the one named, else the profile's for a large size (a keg), else none (a bottle). */
export function containerFor(profile: Profile, ml: number | null, container: unknown): string | undefined {
  if (typeof container === "string" && container !== "") return container;
  return profile.bulk && ml !== null && ml >= profile.bulk.from ? profile.bulk.container : undefined;
}

/**
 * The packs a new article of a size and container starts with: those for its size (or any size)
 * and its container (or any); without a container a drink is in a bottle.
 */
export function defaultPacks(profile: Profile, ml: number, container?: string): { name: string; plural: string; factor: number }[] {
  const c = containerFor(profile, ml, container) ?? "Flasche";
  return profile.packs.filter((p) => (!p.sizes || p.sizes.includes(ml)) && (!p.containers || p.containers.includes(c))).map(({ sizes: _, containers: _c, ...p }) => p);
}
